import React, { useState, useEffect } from 'react';
import { SkillSystem, SKILLS, DIFFICULTY_CLASS } from '../game-system/skills/skillSystem';
import { calculateSkillModifier } from '../game-system/character/calculations';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import './SkillCheckSystemComponent.css';

/**
 * Component for handling skill checks in gameplay
 */
const SkillCheckSystemComponent = ({ 
  character, 
  onSuccess, 
  onFailure, 
  onSkillExperience,
  availableSkills, 
  requiredSkill,
  difficultyClass,
  checkDescription,
  autoCheckOnMount = false
}) => {
  const [selectedSkill, setSelectedSkill] = useState(requiredSkill || '');
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [advantage, setAdvantage] = useState(false);
  const [disadvantage, setDisadvantage] = useState(false);
  const [skillOptions, setSkillOptions] = useState([]);
  
  // Set available skill options
  useEffect(() => {
    if (availableSkills && availableSkills.length > 0) {
      setSkillOptions(availableSkills);
    } else {
      // Default to all skills if none specified
      setSkillOptions(Object.keys(SKILLS));
    }
    
    // If there's a required skill, set it as selected
    if (requiredSkill) {
      setSelectedSkill(requiredSkill);
    }
  }, [availableSkills, requiredSkill]);
  
  // Auto-check if configured
  useEffect(() => {
    if (autoCheckOnMount && requiredSkill && character) {
      handleSkillCheck();
    }
  }, [autoCheckOnMount, requiredSkill, character]);
  
  const handleSkillCheck = () => {
    if (!selectedSkill || !character) return;
    
    const dc = difficultyClass || DIFFICULTY_CLASS.MEDIUM;
    const options = {
      advantage,
      disadvantage
    };
    
    const checkResult = SkillSystem.performCheck(
      character, 
      selectedSkill, 
      dc,
      options
    );
    
    setResult(checkResult);
    
    // Grant experience for the attempt
    if (onSkillExperience) {
      // More XP for harder checks and for success
      const baseXp = Math.floor(dc / 3);
      const successBonus = checkResult.success ? Math.floor(dc / 2) : 0;
      onSkillExperience(selectedSkill, baseXp + successBonus);
    }
    
    // Call appropriate callback
    if (checkResult.success) {
      if (onSuccess) onSuccess(checkResult);
    } else {
      if (onFailure) onFailure(checkResult);
    }
  };
  
  const resetCheck = () => {
    setResult(null);
    setAdvantage(false);
    setDisadvantage(false);
  };
  
  const getResultColor = () => {
    if (!result) return '';
    if (result.criticalSuccess) return 'success-critical';
    if (result.success) return 'success';
    if (result.criticalFailure) return 'failure-critical';
    return 'failure';
  };
  
  const skill = selectedSkill ? SKILLS[selectedSkill.toLowerCase()] : null;
  const skillModifier = selectedSkill ? calculateSkillModifier(character, selectedSkill) : 0;
  
  return (
    <Card className="skill-check-system">
      <CardHeader>
        <CardTitle>
          {checkDescription || 'Skill Check'}
        </CardTitle>
        <CardDescription>
          {skill ? (
            <div className="skill-info">
              <span className="skill-name">{skill.name}</span>
              <span className="skill-attribute">({skill.attribute})</span>
              <span className="skill-modifier">Modifier: {skillModifier >= 0 ? '+' : ''}{skillModifier}</span>
            </div>
          ) : (
            'Select a skill to make a check'
          )}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {!result ? (
          <div className="skill-check-form">
            {!requiredSkill && (
              <div className="skill-selector">
                <label>Choose Skill:</label>
                <select 
                  value={selectedSkill} 
                  onChange={(e) => setSelectedSkill(e.target.value)}
                >
                  <option value="">-- Select Skill --</option>
                  {skillOptions.map(skillKey => (
                    <option key={skillKey} value={skillKey}>
                      {SKILLS[skillKey]?.name} ({calculateSkillModifier(character, skillKey) >= 0 ? '+' : ''}
                      {calculateSkillModifier(character, skillKey)})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="advantage-options">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="advantage"
                  checked={advantage}
                  onChange={() => setAdvantage(!advantage)}
                />
                <label htmlFor="advantage">Advantage</label>
                <span className="tooltip-icon" title="Roll with advantage (roll twice, take the higher roll)">?</span>
              </div>
              
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="disadvantage"
                  checked={disadvantage}
                  onChange={() => setDisadvantage(!disadvantage)}
                />
                <label htmlFor="disadvantage">Disadvantage</label>
                <span className="tooltip-icon" title="Roll with disadvantage (roll twice, take the lower roll)">?</span>
              </div>
            </div>
            
            <Button 
              onClick={handleSkillCheck}
              disabled={!selectedSkill}
              className="check-button"
            >
              Make Check
            </Button>
          </div>
        ) : (
          <div className={`skill-check-result ${getResultColor()}`}>
            <div className="result-header">
              <span className="result-text">
                {result.criticalSuccess && '⭐ Critical Success! ⭐'}
                {result.success && !result.criticalSuccess && '✓ Success'}
                {result.criticalFailure && '❌ Critical Failure! ❌'}
                {!result.success && !result.criticalFailure && '✗ Failure'}
              </span>
              <span className="result-numbers">
                Roll: {result.roll} | Total: {result.total} | DC: {result.difficulty}
              </span>
            </div>
            
            <p className="result-message">{result.details.message}</p>
            
            <div className="result-actions">
              <Button 
                variant="outline" 
                onClick={() => setShowDetails(!showDetails)}
                size="sm"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </Button>
              <Button 
                variant="secondary" 
                onClick={resetCheck}
                size="sm"
              >
                New Check
              </Button>
            </div>
            
            {showDetails && (
              <div className="result-details">
                <table>
                  <tbody>
                    <tr>
                      <td>Base Roll:</td>
                      <td>{result.details.baseRoll}</td>
                    </tr>
                    {result.details.secondRoll !== null && (
                      <tr>
                        <td>Second Roll:</td>
                        <td>{result.details.secondRoll}</td>
                      </tr>
                    )}
                    <tr>
                      <td>Skill Modifier:</td>
                      <td>{result.details.skillModifier}</td>
                    </tr>
                    {result.details.situationalModifiers && result.details.situationalModifiers.map((mod, index) => (
                      <tr key={index}>
                        <td>{mod.name}:</td>
                        <td>{mod.value >= 0 ? '+' : ''}{mod.value}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td>Total:</td>
                      <td>{result.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SkillCheckSystemComponent;